import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { getRunningTimer } from '../redux/ducks/time';
import RunningTimer from './RunningTimer';
import { useHasPermission } from '../utils/permissionUtils';
import { useNavigate } from 'react-router-dom';
import { setTimeTracking } from "../redux/slices/ui";
import TimerButton from './TimerButton';
import { useRetainerStatus } from '../hooks/useRetainerStatus';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const omit = (props) => (prop) => !props.includes(prop);

const TimerBarContainer = styled(Paper, { shouldForwardProp: omit(['navCollapsed']) })(({ theme, navCollapsed }) => ({
    position: 'fixed',
    bottom: 0,
    left: navCollapsed ? theme.spacing(8) : 200,
    right: 0,
    transition: theme.transitions.create('left', {
        easing: theme.transitions.easing.sharp,
        duration: navCollapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen,
    }),
    zIndex: 1300,
    backgroundColor: theme.palette.background.paper,
    backdropFilter: 'blur(12px)',
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 6),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '64px',
    borderTop: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[8],
    [theme.breakpoints.down('sm')]: {
        left: 0,
        zIndex: theme.zIndex.drawer - 1,
    },
}));

const TimerInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(3),
    marginRight: theme.spacing(3),
}));



const ProjectTaskInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
    gap: theme.spacing(0.5),
}));

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
}));

const TaskName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    opacity: 0.9,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
}));

const TimerDisplay = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    minWidth: "fit-content",
    flexDirection: "column",
    gap: 0,
}));

const TimerText = styled(Typography)(({ theme }) => ({
    fontFamily: 'monospace',
    fontSize: theme.typography.size.xxl,
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.size.xlm,
    },
}));

const MetadataRow = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(3),
    color: theme.palette.text.secondary,
    [theme.breakpoints.up('lg')]: {
        display: 'flex',
    },
}));

const MetadataItem = styled(Box)(({ theme }) => ({
    textAlign: 'center',
}));

const MetadataLabel = styled(Typography)(({ theme }) => ({
    textTransform: 'uppercase',
    letterSpacing: 1,
}));

const MetadataValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const TimerBar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUserId = useSelector(state => state.auth.userId);
    const [runningTimer, setRunningTimer] = useState(null);
    const timeTracking = useSelector((state) => state.ui.timeTracking);
    const navCollapsed = useSelector((state) => state.ui.navCollapsed);
    const hasPermission = useHasPermission();
    const canAddTime = hasPermission('addTime');
    const { status: retainerStatus } = useRetainerStatus({ accountId: runningTimer?.account?._id || runningTimer?.account });

    // Fetch running timers for the current user
    useEffect(() => {
        if (!currentUserId || !canAddTime) return;

        dispatch(getRunningTimer((timer) => {
            if (timer) {
                setRunningTimer({
                    _id: timer._id,
                    startTime: new Date(timer.startedAt).getTime(),
                    parentType: timer.parentType,
                    parentId: timer.parentId,
                    summary: timer.summary,
                    client: timer.client,
                    account: timer.account,
                    project: timer.project,
                    task: timer.task
                });
                dispatch(setTimeTracking(true));
            } else {
                setRunningTimer(null);
            }
        }));

    }, [currentUserId, canAddTime, dispatch, timeTracking]);

    const handleItemClick = () => {
        if (runningTimer.project && runningTimer.account) {
            navigate(`/account/${runningTimer.account.slug}/project/${runningTimer.project._id}`);
        }
    };

    if (!runningTimer || !canAddTime) {
        return null;
    }

    return (
        <TimerBarContainer elevation={3} navCollapsed={navCollapsed}>
            <TimerInfo>
                <ProjectTaskInfo>
                    {runningTimer.project && (
                        <ProjectName variant="h6" onClick={handleItemClick}>
                            {runningTimer.project.name}
                        </ProjectName>
                    )}
                    {runningTimer.task && (
                        <TaskName variant="body2" onClick={handleItemClick}>
                            {runningTimer.task.name}
                        </TaskName>
                    )}
                </ProjectTaskInfo>

                <MetadataRow>
                    <MetadataItem>
                        <MetadataLabel variant="caption">
                            Project Due
                        </MetadataLabel>
                        <MetadataValue variant="body2">
                            {formatDate(runningTimer.project?.dueDate)}
                        </MetadataValue>
                    </MetadataItem>
                    <MetadataItem>
                        <MetadataLabel variant="caption">
                            Task Due
                        </MetadataLabel>
                        <MetadataValue variant="body2">
                            {formatDate(runningTimer.task?.dueDate)}
                        </MetadataValue>
                    </MetadataItem>
                    <MetadataItem>
                        <MetadataLabel variant="caption">
                            Started
                        </MetadataLabel>
                        <MetadataValue variant="body2">
                            {formatDateTime(new Date(runningTimer.startTime))}
                        </MetadataValue>
                    </MetadataItem>
                </MetadataRow>

                <TimerDisplay>
                    <TimerText variant="body2">
                        <RunningTimer startTime={runningTimer.startTime} />
                    </TimerText>
                </TimerDisplay>
            </TimerInfo>

            <TimerButton
                parentId={runningTimer.parentId}
                parentType={runningTimer.parentType}
                runningTimerId={runningTimer._id}
                retainerStatus={retainerStatus}
            />
        </TimerBarContainer>
    );
};

export default TimerBar;